import type { BoxProps } from '@chakra-ui/react'
import { Box, Flex, FormControl } from '@chakra-ui/react'
import type { InsuranceParamsPropertyDto } from 'BrokerTsLib/src/dtos'
import { Product, Risk, RiskVariant, Selection } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import React, { useEffect, useState } from 'react'

import { useApp } from '@/hooks/useApp'

import { FormGroup } from '../components/FormGroup'
import { FormLabel } from '../components/FormLabel'
import { FormSpacer } from '../components/FormSpacer'
import { Switch } from '../components/Switch'
import { SwitchControl } from '../components/SwitchControl'
import { useGetRiskVariants } from '../hooks'
import { useInsuranceParamsContext } from '../layouts/Wizard'
import {
  ExtraInsurancePropertyItemIconsEnum,
  getExtraInsurancePropertyIcon,
} from '../utils/getExtraInsurancePropertyIcon'
import type { Step2FormValues } from './chaloupka/Step2Form'
import { CustomNumberInput } from './CustomNumberInput'
import { LiabilitySlider } from './LiabilitySlider'

const ExtraInsuranceItem = ({
  children,
  label,
  tooltip,
  icon,
  sx,
}: {
  children: React.ReactNode
  label: string
  tooltip?: string
  icon: ExtraInsurancePropertyItemIconsEnum | false
  sx?: BoxProps['sx']
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, ...sx }}>
      {icon && getExtraInsurancePropertyIcon(icon, '48px', 'secondary.500')}
      <FormControl sx={{}}>
        <FormLabel tooltip={tooltip}>{label}</FormLabel>
        {children}
      </FormControl>
    </Box>
  )
}

export const ExtraInsuranceProperty = ({ showIcons = true }: { showIcons?: boolean }) => {
  const { product } = useApp()
  const { insuranceParams, insuranceParamsId: paramsId, refetchInsuranceParams } = useInsuranceParamsContext()
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  const { register, getValues, setValue, watch } = useFormContext<Step2FormValues>()

  const { data: liabilityData } = useGetRiskVariants(Risk.RESPONSIBILITY)
  const { data: responsibilityRealestateData } = useGetRiskVariants(Risk.RESPONSIBILITY_REALESTATE)

  const liabilityValues = liabilityData?.filter(l => l.value != '0').map((x, i) => { return { index: i, item: x } }) ?? []
  const responsibilityRealestateValues = responsibilityRealestateData?.filter(l => l.value != '0').map((x, i) => { return { index: i, item: x } }) ?? []

  // const propertyOwnershipLiabilityMin = 0
  // const propertyOwnershipLiabilityMax = 50000000
  // const propertyOwnershipLiabilityStep = 1000000
  // const lifeLiabilityMin = 0
  // const lifeLiabilityMax = 50000000
  // const lifeLiabilityStep = 1000000
  const insuredHouseholdValueMin = 100000
  const insuredHouseholdValueMax = 5000000
  const insuredHouseholdValueStep = 100000
  const insuredRealtyValueMin = 500000
  const insuredRealtyValueMax = 50000000
  const insuredRealtyValueStep = 500000

  const selectedLiability = watch('property.liability')
  const selectedResponsibilityRealestate = watch('property.responsibilityRealestate')
  const floodInsuranceRequired = watch('property.data.floodInsuranceRequired')

  const initialLiabilityChecked = !(!selectedLiability || selectedLiability == RiskVariant.PropertyWithoutLiability)
  const initialResponsibilityRealestateChecked = !(!selectedResponsibilityRealestate || selectedResponsibilityRealestate == RiskVariant.ResponsibilityRealestate_Without)

  const [liabilityChecked, setLiabilityChecked] = useState(initialLiabilityChecked)
  const [responsibilityRealestateChecked, setResponsibilityRealestateChecked] = useState(initialResponsibilityRealestateChecked)

  const realtyTypeId = getValues('property.data.realtyTypeId')

  useEffect(() => {
    if (!liabilityChecked)
      setValue('property.liability', undefined)
  }, [liabilityChecked])

  useEffect(() => {
    if (!responsibilityRealestateChecked)
      setValue('property.responsibilityRealestate', undefined)
  }, [responsibilityRealestateChecked])


  const householdDisabled = ((insuranceParams as InsuranceParamsPropertyDto).property.data.household?.amount ?? 0) == 0
  const realtyDisabled = ((insuranceParams as InsuranceParamsPropertyDto).property.data.realty?.amountSum ?? 0) == 0

  return (
    <Box sx={{ w: '100%', display: 'grid', gap: 4 }}>
      {product == Product.PropertyInsurance &&
        <>
          <Flex sx={{ p: 4, bgColor: 'blue.50', borderRadius: 'md' }}>
            <Box
              sx={{ display: 'grid', gap: 8, gridTemplateColumns: { base: '1fr', md: '1.2fr 2fr' } }}
            >
              <FormControl>
                <FormLabel>Pojistná částka nemovitosti</FormLabel>

                <CustomNumberInput
                  defaultValue={watch('property.data.realty.realtyAmount')}
                  isDisabled={isContracted || realtyDisabled}
                  onValueChange={(value) => setValue('property.data.realty.realtyAmount', value ? parseInt(value) : 0)}
                />
              </FormControl>

              <LiabilitySlider
                name="property.data.realty.realtyAmount"
                min={insuredRealtyValueMin}
                max={insuredRealtyValueMax}
                step={insuredRealtyValueStep}
                defaultValue={watch('property.data.realty.realtyAmount') ?? undefined}
                onChange={(value) => setValue('property.data.realty.realtyAmount', value)}
                isDisabled={isContracted || realtyDisabled}
              />
            </Box>
            <Box
              sx={{ display: 'grid', gap: 8, gridTemplateColumns: { base: '1fr', md: '1.2fr 2fr' } }}
            >
              <FormControl>
                <FormLabel>Pojistná částka domácnosti</FormLabel>

                <CustomNumberInput
                  defaultValue={watch('property.data.household.amount')}
                  onValueChange={(value) => setValue('property.data.household.amount', value ? parseInt(value) : 0)}
                  isDisabled={isContracted || householdDisabled}
                />
              </FormControl>

              <LiabilitySlider
                name="property.data.household.amount"
                min={insuredHouseholdValueMin}
                max={insuredHouseholdValueMax}
                step={insuredHouseholdValueStep}
                defaultValue={watch('property.data.household.amount') ?? undefined}
                onChange={(value) => setValue('property.data.household.amount', value)}
                isDisabled={isContracted || householdDisabled}
              />
            </Box>
          </Flex>

          <FormSpacer />

          <FormGroup sx={{ w: '100%', flexWrap: 'nowrap' }}>
            <ExtraInsuranceItem
              label="Povodeň, záplava"
              tooltip="Povodní se rozumí zaplavení územních celků vodou, která se vylila z břehů vodních toků nebo nádrží nebo která břehy a hráze protrhla a nebo bylo zaplavení způsobeno náhlým a náhodným zmenšením průtočného profilu vodního toku. Záplavou se rozumí vytvoření souvislé vodní plochy, která po určitou dobu stojí nebo proudí v místě, např. vystoupnutím odpadních vod z kanalizačního nebo odpadního potrubí následkem povodně a záplavy.          "
              icon={showIcons && ExtraInsurancePropertyItemIconsEnum.Flood}
              sx={{ minWidth: '22rem' }}
            >
              <Switch
                {...register('property.data.floodInsuranceRequired')}
                isDisabled={isContracted}
              />
            </ExtraInsuranceItem>

            {floodInsuranceRequired && (
              <Box
                sx={{ display: 'grid', gap: 8, gridTemplateColumns: { base: '1fr', md: '1fr 1.8fr' } }}
              >
                <FormControl>
                  <FormLabel
                    tooltip='Kolikrát se v místě pojištění v posledních 20 letech vyskytla povodeň nebo záplava? Pokud se záplava nebo povodeň vyskytla více než jednou, dojde u Kooperativy k vyloučení škod vzniklých působením pojistných nebezpečí povodeň nebo záplava.'
                  >
                    Počet povodní v uplynulých 20 letech
                  </FormLabel>

                  <CustomNumberInput
                    min={0}
                    max={100}
                    defaultValue={getValues('property.data.floodOccurence')}
                    onValueChange={(_, value) => setValue('property.data.floodOccurence', value)}
                    isDisabled={isContracted}
                    inputRightElement={null}
                  />
                </FormControl>

                {realtyTypeId == Selection.Byt && (
                  <FormControl>
                    <FormLabel tooltip="Nachází se byt pojištěného v bezpečném podlaží bytového domu (tzn. minimálně 3m nad úrovní okolního terénu)? Pouze pro pojišťovnu Kooperativa.">
                      Bezpečné podlaží vůči povodni
                    </FormLabel>

                    <SwitchControl>
                      <Switch
                        {...register('property.data.floodSafeAppartment')}
                        isChecked={watch('property.data.floodSafeAppartment') ?? false}
                        isDisabled={isContracted}
                      />
                    </SwitchControl>
                  </FormControl>
                )}
              </Box>
            )}
          </FormGroup>

          <FormSpacer />
        </>
      }

      <FormGroup sx={{ w: '100%' }}>
        <ExtraInsuranceItem
          label="Odpovědnost z běžného života"
          tooltip='Pojištění odpovědnosti z činností v běžném životě - kryje újmy/škody na zdraví, na věci a finanční škody. Zvolte minimální výši limitu, který požadujete. V případě, že pojišťovna nabízí jiný limit, tak bude nastaveno na nejbližší vyšší limit.'
          icon={showIcons && ExtraInsurancePropertyItemIconsEnum.LifeLiability}
          sx={{ minWidth: '22rem' }}
        >
          <Switch
            isChecked={liabilityChecked}
            onChange={(e => setLiabilityChecked(e.target.checked))}
            name={'liabilityChecked'}
            isDisabled={isContracted}
          />
        </ExtraInsuranceItem>

        {liabilityChecked && liabilityValues.length > 0 && (
          <Box
            sx={{ display: 'grid', gap: 8, gridTemplateColumns: { base: '1fr', md: '1fr 1.8fr' } }}
          >
            <FormControl>
              <FormLabel>Limit plnění</FormLabel>

              <CustomNumberInput
                defaultValue={parseInt(liabilityValues.find(x => x.item.id == selectedLiability)?.item.value ?? '0')}
                isDisabled
              />
            </FormControl>

            <LiabilitySlider
              name="lifeLiabilityValue"
              min={0}
              minLabel={priceFormat(liabilityValues[0].item.value)}
              maxLabel={priceFormat(liabilityValues[liabilityValues.length - 1].item.value)}
              max={liabilityValues.length - 1}
              step={1}
              defaultValue={liabilityValues.find(x => x.item.id == selectedLiability)?.index ?? 0}
              onChange={(value) => setValue('property.liability', liabilityValues.find(x => x.index == value)?.item.id)}
              isDisabled={isContracted}
            />
          </Box>
        )}
      </FormGroup>

      <FormGroup sx={{ w: '100%' }}>
        <ExtraInsuranceItem
          label="Odpovědnost z vlastnictví nemovitosti"
          tooltip='Pojištění odpovědnosti vlastníka nemovitosti za škody z držby nemovitosti - kryje újmy/škody na zdraví, na věci a finanční škody. Zvolte minimální výši limitu, který požadujete. V případě, že pojišťovna nabízí jiný limit, tak bude nastaveno na nejbližší vyšší limit.'
          icon={showIcons && ExtraInsurancePropertyItemIconsEnum.PropertyOwnershipLiability}
          sx={{ minWidth: '22rem' }}
        >
          <Switch
            isChecked={responsibilityRealestateChecked}
            onChange={(e => setResponsibilityRealestateChecked(e.target.checked))}
            name={'responsibilityRealestateChecked'}
            isDisabled={isContracted}
          />
        </ExtraInsuranceItem>

        {responsibilityRealestateChecked && responsibilityRealestateValues.length > 0 && (
          <Box
            sx={{ display: 'grid', gap: 8, gridTemplateColumns: { base: '1fr', md: '1fr 1.8fr' } }}
          >
            <FormControl>
              <FormLabel>Limit plnění</FormLabel>

              <CustomNumberInput
                defaultValue={parseInt(responsibilityRealestateValues.find(x => x.item.id == selectedResponsibilityRealestate)?.item.value ?? '0')}
                isDisabled
              />
            </FormControl>

            <LiabilitySlider
              name="responsibilityRealestateValue"
              min={0}
              minLabel={priceFormat(responsibilityRealestateValues[0].item.value)}
              maxLabel={priceFormat(responsibilityRealestateValues[responsibilityRealestateValues.length - 1].item.value)}
              max={responsibilityRealestateValues.length - 1}
              step={1}
              defaultValue={responsibilityRealestateValues.find(x => x.item.id == selectedResponsibilityRealestate)?.index ?? 0}
              onChange={(value) => setValue('property.responsibilityRealestate', responsibilityRealestateValues.find(x => x.index == value)?.item.id)}
              isDisabled={isContracted}
            />
          </Box>
        )}
      </FormGroup>

      <FormSpacer />
    </Box>
  )
}
